import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarChart } from '../../components/charts/BarChart';
import { chartColour, topN } from '../../components/charts/palette';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonTable } from '../../components/Skeleton';
import { type ReportFilters } from './api';
import { formatReportValue, reportDescription, reportTitle } from './presentation';
import { SchedulePanel } from './SchedulePanel';
import { SurveyPanel } from './SurveyPanel';
import { useReport, useReportCatalog, useReportExport } from './queries';
import { Select } from '../../components/Select';

/**
 * Die Berichte -- F5-06 bis F5-08.
 *
 * **Die Auswahl kommt vom Server.** Welche Berichte es gibt und welche Achsen
 * jeder kennt, steht in `/reports`; diese Datei fuehrt keine eigene Liste.
 * Eine zweite Liste liefe auseinander, sobald ein Bericht dazukommt -- und
 * der Anwender saehe eine Achse, die der Server abweist.
 *
 * **Die Einheit steht an der Spalte und nicht im Text daneben.** "42" ist
 * ohne Einheit wertlos; ob es Tickets, Minuten oder Prozent sind,
 * entscheidet der Bericht, und die Anzeige rundet und beschriftet danach.
 *
 * **Das Bild steht ueber der Tabelle, nicht statt ihrer.** Balken zeigen das
 * Verhaeltnis in einem Blick, aber niemand liest einen genauen Wert von
 * einem Balken ab -- und in einer Auswertung, die jemandem vorgelegt wird,
 * ist der genaue Wert der Punkt. Das Diagramm zeigt darum die groessten
 * zehn, die Tabelle darunter jede Zeile; dass gekuerzt wurde, steht
 * ausdruecklich dabei.
 */
export function ReportsPage() {
  const { t } = useTranslation();
  const catalog = useReportCatalog();

  const [key, setKey] = useState('ticket_volume');
  const [axis, setAxis] = useState<string | undefined>(undefined);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');

  const filters: ReportFilters = {
    ...(since ? { since } : {}),
    ...(until ? { until } : {}),
  };

  const definition = catalog.data?.find((entry) => entry.key === key);
  const effectiveAxis = axis ?? definition?.default_axis;
  const report = useReport(key, effectiveAxis, filters);
  const exportFile = useReportExport();

  return (
    <>
      <PageHeader
        title={t('reports.title')}
        subtitle={definition ? reportDescription(t, definition) : t('reports.subtitle')}
        action={
          <div className="row">
            <Button
              variant="secondary"
              busy={exportFile.isPending && exportFile.variables.format === 'csv'}
              onClick={() => {
                exportFile.mutate({ key, axis: effectiveAxis, filters, format: 'csv' });
              }}
            >
              {t('reports.downloadCsv')}
            </Button>
            <Button
              variant="secondary"
              busy={exportFile.isPending && exportFile.variables.format === 'pdf'}
              onClick={() => {
                exportFile.mutate({ key, axis: effectiveAxis, filters, format: 'pdf' });
              }}
            >
              {t('reports.downloadPdf')}
            </Button>
          </div>
        }
      />

      {exportFile.isError ? <ErrorNotice error={exportFile.error} /> : null}

      <section className="card">
        <div className="row">
          <div className="field">
            <label className="field__label" htmlFor="report-key">
              {t('reports.report')}
            </label>
            <Select
              id="report-key"
              className="field__control"
              value={key}
              onChange={(event) => {
                setKey(event.target.value);
                // Die Achse gehoert zum Bericht: Der naechste kennt sie
                // vielleicht nicht, und der Server weist sie dann ab.
                setAxis(undefined);
              }}
            >
              {(catalog.data ?? []).map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {reportTitle(t, entry)}
                </option>
              ))}
            </Select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="report-axis">
              {t('reports.axis')}
            </label>
            <Select
              id="report-axis"
              className="field__control"
              value={effectiveAxis ?? ''}
              onChange={(event) => {
                setAxis(event.target.value);
              }}
            >
              {(definition?.axes ?? []).map((value) => (
                <option key={value} value={value}>
                  {t(`reportAxis.${value}`)}
                </option>
              ))}
            </Select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="report-since">
              {t('time.from')}
            </label>
            <input
              id="report-since"
              type="date"
              className="field__control"
              value={since}
              onChange={(event) => {
                setSince(event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="report-until">
              {t('time.to')}
            </label>
            <input
              id="report-until"
              type="date"
              className="field__control"
              value={until}
              onChange={(event) => {
                setUntil(event.target.value);
              }}
            />
          </div>
        </div>
      </section>

      {/* Der Versand steht unter den Zahlen und nicht in der Verwaltung: Wer
          einen Bericht einrichtet, hat ihn gerade angesehen -- F5-09. */}
      <details>
        <summary className="table__subject">{t('schedules.title')}</summary>
        <SchedulePanel />
      </details>

      {/* Der Rueckstand der Umfragen steht bei der Zufriedenheit und
          nirgendwo sonst: Er ist die Grundlage, ohne die der Mittelwert
          darueber nichts aussagt -- F5-10. */}
      {key === 'satisfaction' ? <SurveyPanel /> : null}

      {report.data && report.data.rows.length > 0 ? (
        <section className="card">
          <div className="chart-card__head">
            <h2>{definition ? reportTitle(t, definition) : t('reports.title')}</h2>
            <p className="chart-card__hint">{t('reports.chartHint')}</p>
          </div>
          <BarChart
            caption={definition ? reportTitle(t, definition) : t('reports.title')}
            // Prozentwerte bekommen eine feste Obergrenze: Bei einem Bericht,
            // dessen groesster Wert 60 % ist, saehen sonst 60 % aus wie
            // "vollstaendig eingehalten".
            max={report.data.unit === 'percent' ? 100 : undefined}
            data={topN(
              report.data.rows.map((row) => ({
                key: row.key,
                label: row.label,
                value: row.value,
              })),
              10,
              (value, count) => ({
                key: '__rest__',
                label: t('charts.other', { count }),
                value,
              }),
            ).map((row, index) => ({
              ...row,
              display: formatReportValue(row.value, report.data.unit),
              colour: row.key === '__rest__' ? 'var(--chart-8)' : chartColour(index),
            }))}
          />
        </section>
      ) : null}

      <section className="card card--flush">
        {report.isPending ? <SkeletonTable columns={3} /> : null}
        {report.isError ? <ErrorNotice error={report.error} /> : null}

        {report.data?.rows.length === 0 ? (
          <EmptyState icon="reports" title={t('reports.noData')} hint={t('reports.noDataHint')} />
        ) : null}

        {report.data && report.data.rows.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t(`reportAxis.${report.data.axis}`)}</th>
                  <th scope="col">{t(`reportUnit.${report.data.unit}`)}</th>
                  {report.data.with_reference ? (
                    <th scope="col">{t('reports.reference')}</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {report.data.rows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td className="table__number">
                      {formatReportValue(row.value, report.data.unit)}
                    </td>
                    {report.data.with_reference ? (
                      <td className="table__number">{row.reference}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}
